// ./src/components/features/CreateEventWizard/steps/VolunteersStep.tsx
//
// "Who do you need helping?" — shown when the Volunteers & staff feature is on (ROADMAP A2b #62 P6(f)). Each shift is made
// in the Volunteers page's own `VolunteerSlotEditDrawer` (role, how many, when, where, rewards, pay), under a provider
// scoped by the event — the drawer saves THROUGH that provider and does nothing without it.
//
// ⚠ The drawer edits a shift's times in the EVENT's timezone, so the step reads the event first and the list (and Add)
//   wait for that read — a shift typed in the browser's zone would land hours off for a host abroad (the Tasks step's rule).
// ⓘ Before Budget: paid shifts make the budget's "Staff pay" line. Before the Event page step, which offers the
//   "Volunteer" block for the shifts open to sign-ups.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Stack, Text } from '@mantine/core';
import { OpResult } from '@sdflc/api-helpers';
import { useTranslation } from 'react-i18next';

import { FEATURE_CODES } from '@/config';
import { VolunteerSlotsProvider, useVolunteerSlotsContext } from '@/contexts';
import { useCreateGate, useEvent, useToast } from '@/hooks';
import { VolunteerSlot, WHOLE_SET } from '@/utils/api';
import { formatEventDateTimeRange, refusalReason } from '@/utils';
import { ConfirmModal } from '@/components/generic';
import { VolunteerSlotEditDrawer } from '@/components/features/VolunteerSlots';

import { useEventWizard } from '../WizardContext';
import { WizardItemRow } from '../components/WizardItemRow';
import { WizardListSection } from '../components/WizardListSection';

/** Soonest first; a shift with no time after every timed one, then the host's order. */
const byTimeThenOrder = (a: VolunteerSlot, b: VolunteerSlot): number => {
  if (a.startsAt && b.startsAt && a.startsAt !== b.startsAt) {
    return a.startsAt.localeCompare(b.startsAt);
  }

  if (!a.startsAt !== !b.startsAt) {
    return a.startsAt ? -1 : 1;
  }

  return (a.orderNo ?? 0) - (b.orderNo ?? 0);
};

const VolunteersStepBody: React.FC<{ eventId: string }> = ({ eventId }) => {
  const { t } = useTranslation(['events', 'common']);
  const toast = useToast();
  const { saving } = useEventWizard();
  const { volunteerSlots, list, isLoaded, remove } = useVolunteerSlotsContext();
  const { event, error: eventError, reload: reloadEvent } = useEvent(eventId);
  // Volunteers & staff are the `production` bundle — the Volunteers page asks the same.
  const gate = useCreateGate(FEATURE_CODES.PRODUCTION, { eventId });

  const [readError, setReadError] = useState<OpResult | null>(null);
  const [drawer, setDrawer] = useState<{ recordId: string | null } | null>(null);
  const [pendingRemove, setPendingRemove] = useState<VolunteerSlot | null>(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    const res = await list({ eventId: [eventId] }, { page: 1, pageSize: WHOLE_SET }, undefined, true);

    setReadError(res.didSucceed() ? null : res);
  }, [list, eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  const sorted = useMemo(() => [...volunteerSlots].sort(byTimeThenOrder), [volunteerSlots]);

  const subtitleOf = (slot: VolunteerSlot): string => {
    const when = formatEventDateTimeRange(slot.startsAt, slot.endsAt, event?.timezone) || t('events:wizard.volunteers.noTime', { defaultValue: 'No time yet' });
    const needed = t('events:wizard.volunteers.needed', { defaultValue: '{{count}} needed', count: Math.max(1, slot.quantityNeeded ?? 1) });

    return `${when} · ${needed}`;
  };

  const afterSave = () => {
    setDrawer(null);
    void load();
  };

  const confirmRemove = async () => {
    if (!pendingRemove?.id) {
      return;
    }

    setRemoving(true);

    const res = await remove(pendingRemove.id);

    setRemoving(false);

    if (!res.didSucceed()) {
      toast.error(refusalReason(res));

      return;
    }

    setPendingRemove(null);
    void load();
  };

  return (
    <Stack gap="lg">
      <Text size="md" c="dimmed">
        {t('events:wizard.volunteers.intro', {
          defaultValue:
            'Add the shifts you need people for — what, when and how many. A shift open to sign-ups can be shown on your event page (the Event page step offers a “Volunteer” block); you approve people and check them in from the event’s Volunteers pages.',
        })}
      </Text>

      <WizardListSection
        title={t('events:wizard.volunteers.title', { defaultValue: 'Volunteer shifts' })}
        addLabel={t('events:wizard.volunteers.add', { defaultValue: 'Add a shift' })}
        onAdd={() => gate.canCreate && setDrawer({ recordId: null })}
        gate={gate}
        gateWhat={t('events:wizard.volunteers.another', { defaultValue: 'another shift' })}
        disabled={saving || !event}
        isLoaded={(isLoaded || !!readError) && (!!event || !!eventError)}
        readError={readError ?? eventError}
        onRetry={() => {
          void reloadEvent();
          void load();
        }}
        errorTitle={t('events:wizard.volunteers.loadFailed', { defaultValue: 'Could not load the shifts' })}
        operation="volunteerSlotList"
        emptyText={t('events:wizard.volunteers.none', { defaultValue: 'No shifts yet.' })}
        isEmpty={sorted.length === 0}
      >
        {sorted.map((slot) => (
          <WizardItemRow
            key={slot.id}
            icon="HeartHandshake"
            title={slot.name ?? ''}
            subtitle={subtitleOf(slot)}
            disabled={saving}
            onEdit={() => setDrawer({ recordId: slot.id as string })}
            onRemove={() => setPendingRemove(slot)}
          />
        ))}
      </WizardListSection>

      <VolunteerSlotEditDrawer
        opened={!!drawer}
        onClose={() => setDrawer(null)}
        eventId={eventId}
        recordId={drawer?.recordId ?? null}
        eventTimezone={event?.timezone ?? null}
        siblings={volunteerSlots}
        onCreated={afterSave}
        onUpdated={afterSave}
      />

      <ConfirmModal
        opened={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        onConfirm={() => void confirmRemove()}
        type="danger"
        confirmInProgress={removing}
        title={t('events:wizard.volunteers.removeTitle', { defaultValue: 'Remove this shift?' })}
        message={t('events:wizard.tickets.removeMessage', { defaultValue: '“{{name}}” will be removed from this event.', name: pendingRemove?.name ?? '' })}
        confirmLabel={t('common:actions.remove', { defaultValue: 'Remove' })}
      />
    </Stack>
  );
};

export const VolunteersStep: React.FC = () => {
  const { t } = useTranslation(['events']);
  const { createdEventId } = useEventWizard();

  if (!createdEventId) {
    return (
      <Text size="md" c="dimmed">
        {t('events:wizard.tickets.noEvent', { defaultValue: 'Your event is created at the end of the Guests step.' })}
      </Text>
    );
  }

  return (
    <VolunteerSlotsProvider initialFilters={{ eventId: [createdEventId] }} pageSize={WHOLE_SET} autoLoad={false}>
      <VolunteersStepBody eventId={createdEventId} />
    </VolunteerSlotsProvider>
  );
};
